// src/frontend_react/src/pages/AdminDashboard/ProjectManagementPage.js
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import Select from 'react-select';
import makeAnimated from 'react-select/animated';
import { Link } from 'react-router-dom';

// Base UI Components
import Button from '../../components/ui/Button/Button';
import Table from '../../components/ui/Table/Table';
import Input from '../../components/ui/Input/Input';
import Checkbox from '../../components/ui/Checkbox/Checkbox';
import Alert from '../../components/ui/Alert/Alert';
import Spinner from '../../components/ui/Spinner/Spinner';
import Card from '../../components/ui/Card/Card';
import Modal from '../../components/ui/Modal/Modal';
import Icon from '../../components/ui/Icon/Icon';
import Typography from '../../components/ui/Typography/Typography';
import Stack, { HStack } from '../../components/ui/Stack/Stack';
import Box from '../../components/ui/Box/Box';
import Badge from '../../components/ui/Badge/Badge';
import Stepper from '../../components/ui/Stepper/Stepper';
import EmptyState from '../../components/ui/EmptyState/EmptyState'; // Import EmptyState

import { notify } from '../../services/notificationService';


// API service
import {
  getAdminProjects,
  createAdminProject,
  updateAdminProject,
  deleteAdminProject,
  getAdminDataSourceTypes,
  generateAdminSecret,
} from '../../services/api';

// Icons
import {
  FiBriefcase, FiPlus, FiEdit, FiToggleLeft, FiToggleRight,
  FiKey, FiFileText, FiDatabase
} from 'react-icons/fi';
import adminPageStyles from './styles.module.css';

const animatedComponents = makeAnimated();

function ProjectManagementPage() {
  const [projects, setProjects] = useState([]);
  const [allDataSourceTypes, setAllDataSourceTypes] = useState([]);
  const [isLoadingData, setIsLoadingData] = useState(true);
  const [error, setError] = useState(null);
  const [formError, setFormError] = useState(null);

  // Modal & Wizard State
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [wizardStep, setWizardStep] = useState(1);
  const [currentProject, setCurrentProject] = useState({
    id: '', name: '', description: '', data_source_type_ids: [], is_active: true,
  });

  // Deactivation Modal State
  const [showDeactivateConfirm, setShowDeactivateConfirm] = useState(false);
  const [projectToProcess, setProjectToProcess] = useState(null);
  const [confirmationInput, setConfirmationInput] = useState('');

  const fetchProjectsAndDSTs = useCallback(async () => {
    setIsLoadingData(true);
    setError(null);
    try {
      const [projectsData, dstData] = await Promise.all([getAdminProjects(), getAdminDataSourceTypes()]);
      setProjects(Array.isArray(projectsData) ? projectsData.sort((a, b) => a.name.localeCompare(b.name)) : []);
      setAllDataSourceTypes(Array.isArray(dstData) ? dstData.sort((a, b) => a.display_name.localeCompare(b.display_name)) : []);
    } catch (err) {
      setError(err.message || 'Failed to fetch initial data.');
    } finally {
      setIsLoadingData(false);
    }
  }, []);

  useEffect(() => {
    fetchProjectsAndDSTs();
  }, [fetchProjectsAndDSTs]);

  const resetFormState = () => {
    setCurrentProject({ id: '', name: '', description: '', data_source_type_ids: [], is_active: true });
    setFormError(null);
    setWizardStep(1);
  };

  const handleCreateNew = () => {
    setIsEditMode(false);
    resetFormState();
    setIsModalOpen(true);
  };

  const handleEdit = (project) => {
    setIsEditMode(true);
    resetFormState();
    const projectDstIds = project.data_source_types ? project.data_source_types.map(dst => dst.id) : (project.data_source_type_ids || []);
    setCurrentProject({ ...project, data_source_type_ids: projectDstIds });
    setIsModalOpen(true);
  };
  
  const openDeactivateConfirm = (project) => {
    setProjectToProcess(project);
    setConfirmationInput(''); // Reset input on open
    setShowDeactivateConfirm(true);
  };

  const handleDeactivateReactivate = useCallback(async (projectToToggle, newActiveState) => {
    if (!projectToToggle) return;
    setIsSubmitting(true);
     setError(null);
    const actionVerb = newActiveState ? 'reactivate' : 'deactivate';
    try {
      if (!newActiveState) {
        await deleteAdminProject(projectToToggle.id);
      } else {
        await updateAdminProject(projectToToggle.id, { is_active: true });
      }
      notify.success(`Project '${projectToToggle.name}' ${actionVerb}d successfully.`);
      fetchProjectsAndDSTs();
    } catch (err) {
      setError(err.response?.data?.message || `Failed to ${actionVerb} project '${projectToToggle.name}'.`);
    } finally {
      setIsSubmitting(false);
      setShowDeactivateConfirm(false);
    }
  }, [fetchProjectsAndDSTs]);

  const handleFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setCurrentProject(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleDataSourceSelectChange = (selectedOptions) => {
    const selectedIds = selectedOptions ? selectedOptions.map(opt => opt.value) : [];
    setCurrentProject(prev => ({ ...prev, data_source_type_ids: selectedIds }));
  };
  
  const validateStep1 = () => {
    if (!currentProject.id.trim()) { setFormError('Project ID is required.'); return false; }
    if (!/^[a-zA-Z0-9_.-]+$/.test(currentProject.id.trim())) { setFormError('Project ID can only contain letters, numbers, underscores, hyphens, and periods.'); return false; }
    if (!currentProject.name.trim()) { setFormError('Project Name is required.'); return false; }
    setFormError(null);
    return true;
  };
  
  const handleWizardNext = () => {
    if (validateStep1()) {
      setWizardStep(2);
    }
  };
  
  const handleUpdateSubmit = async () => {
    if (!currentProject.name.trim()) { setFormError('Project Name is required.'); return; }
    setFormError(null); setIsSubmitting(true);
    const payload = {
        name: currentProject.name.trim(), description: currentProject.description.trim(),
        data_source_type_ids: currentProject.data_source_type_ids, is_active: currentProject.is_active,
    };
    try {
        await updateAdminProject(currentProject.id, payload);
        notify.success(`Project '${payload.name}' updated successfully.`);
        setIsModalOpen(false);
        fetchProjectsAndDSTs();
    } catch (err) {
        setFormError(err.response?.data?.message || "Failed to update project.");
    } finally {
        setIsSubmitting(false);
    }
  };
  
  const handleWizardSubmit = async () => {
    if (isSubmitting || !validateStep1()) return;
    setIsSubmitting(true); setFormError(null);
    setWizardStep(2); // Visually move to processing step
    
    try {
        const payload = { ...currentProject, id: currentProject.id.trim(), name: currentProject.name.trim() };
        const createdProject = await createAdminProject(payload);
        
        await Promise.all([
            generateAdminSecret(createdProject.id, 'SHARED_SALT'),
            generateAdminSecret(createdProject.id, 'OUTPUT_ENCRYPTION_KEY'),
            generateAdminSecret(createdProject.id, 'PROJECT_NONCE'),
        ]);
        
        notify.success(`Project '${createdProject.name}' and its security keys were created successfully.`);
        setIsModalOpen(false);
        fetchProjectsAndDSTs();
    } catch (err) {
        setFormError(err.response?.data?.message || "An error occurred during project creation or key generation.");
        setWizardStep(1);
    } finally {
        setIsSubmitting(false);
    }
  };
  
  const columns = useMemo(() => [
    { Header: 'ID', accessor: 'id', width: 200 },
    { Header: 'Name', accessor: 'name' },
    { Header: 'Data Sources', accessor: 'data_source_display_names', Cell: ({ value }) => value?.length ? <Typography variant="body2" color="secondary">{value.join(', ')}</Typography> : <Typography variant="caption" color="placeholder">None</Typography> },
    { Header: 'Status', accessor: 'is_active', width: 100, Cell: ({ value }) => <Badge variant={value ? 'success' : 'default'} size="sm">{value ? 'Active' : 'Inactive'}</Badge> },
    { Header: 'Actions', 
      id: 'actions', 
      width: 250, 
      Cell: ({ row }) => ( // Corrected destructuring: we get 'row' directly
        <HStack spacing="sm">
          <Button variant="outline" size="sm" onClick={() => handleEdit(row)} leftIcon={<Icon as={FiEdit} />} disabled={isSubmitting || isLoadingData}>Edit</Button>
          <Button variant={row.is_active ? "warning" : "success"} size="sm" onClick={() => openDeactivateConfirm(row)} leftIcon={<Icon as={row.is_active ? FiToggleLeft : FiToggleRight} />} disabled={isSubmitting || isLoadingData}>
            {row.is_active ? 'Deactivate' : 'Reactivate'}
          </Button>
        </HStack>
      )
    },
  ], [isSubmitting, isLoadingData, handleEdit, openDeactivateConfirm, handleDeactivateReactivate]);

  const dstOptionsForSelect = allDataSourceTypes.map(dst => ({ value: dst.id, label: dst.display_name }));
  const selectedDstOptions = dstOptionsForSelect.filter(opt => currentProject.data_source_type_ids.includes(opt.value));
  const wizardSteps = [{ id: 1, label: "Project Details" }, { id: 2, label: "Secure & Finish" }];

  const renderFormContent = () => (
    <Stack spacing="lg" as="form" id="project-form" onSubmit={(e) => { e.preventDefault(); isEditMode && handleUpdateSubmit(); }}>
        <Input label="Project ID" name="id" value={currentProject.id} onChange={handleFormChange} required disabled={isSubmitting || isEditMode} hintText="Unique identifier (e.g., project_alpha). Cannot be changed." />
        <Input label="Project Name" name="name" value={currentProject.name} onChange={handleFormChange} required disabled={isSubmitting} />
        <Input label="Description" name="description" value={currentProject.description} onChange={handleFormChange} isTextarea rows={3} disabled={isSubmitting} />
        <Box>
          <Typography as="label" htmlFor="project-data-source-types-select" fontWeight="medium" mb="xs" display="block">
            Participating Data Source Types
          </Typography>
          {allDataSourceTypes.length > 0 ? (
            <Select
              inputId="project-data-source-types-select"
              value={selectedDstOptions}
              onChange={handleDataSourceSelectChange}
              options={dstOptionsForSelect}
              isMulti
              closeMenuOnSelect={false}
              components={animatedComponents}
              isDisabled={isSubmitting}
            />
          ) : (
            <Alert variant="info" title="No Data Source Types Available" icon={<Icon as={FiDatabase} />}>
              <Typography>
                You must create at least one Data Source Type before you can create a project.
                <Link to="/admin/dashboard/datasourcetypes" style={{ marginLeft: '8px', fontWeight: '600' }}>
                  Go to Data Source Types
                </Link>
              </Typography>
            </Alert>
          )}
        </Box>
        {isEditMode && <Checkbox label="Project is Active" name="is_active" checked={currentProject.is_active} onChange={handleFormChange} disabled={isSubmitting} />}
    </Stack>
  );

  const renderSecurityStep = () => (
    <Stack spacing="md">
        <Alert variant="info" title="Final Step: Secure Project">Clicking "Generate Keys & Create" will finalize the project and generate its essential cryptographic keys.</Alert>
        {isSubmitting ? (
          <Stack alignItems="center" p="xl"><Spinner size="lg" /><Typography mt="md" color="secondary">Securing project...</Typography></Stack>
        ) : (
          <Card elevation="sm" className={adminPageStyles.adminNestedActionCard}>
              <Stack p="md" spacing="sm">
                  <HStack justifyContent="space-between" alignItems="center"><HStack spacing="sm" alignItems="center"><Icon as={FiKey} /><Typography>SHARED_SALT</Typography></HStack><Badge>To be generated</Badge></HStack>
                  <HStack justifyContent="space-between" alignItems="center"><HStack spacing="sm" alignItems="center"><Icon as={FiKey} /><Typography>OUTPUT_ENCRYPTION_KEY</Typography></HStack><Badge>To be generated</Badge></HStack>
                  <HStack justifyContent="space-between" alignItems="center"><HStack spacing="sm" alignItems="center"><Icon as={FiKey} /><Typography>PROJECT_NONCE</Typography></HStack><Badge>To be generated</Badge></HStack>
              </Stack>
          </Card>
        )}
    </Stack>
  );

  return (
    <Card title="Project Management" titleIcon={<Icon as={FiBriefcase} />} headerActions={!isModalOpen && <Button variant="primary" onClick={handleCreateNew} leftIcon={<Icon as={FiPlus} />}>Create New Project</Button>}>
      <Stack spacing="lg">
        <Typography variant="body1" color="secondary">Define and manage data linkage projects, their configurations, and associated data source types.</Typography>
        {error && <Alert variant="error" onDismiss={() => setError(null)}>{error}</Alert>}
        
        <Table 
            columns={columns} 
            data={projects} 
            isLoading={isLoadingData}
            emptyStateTitle="No Projects Found"
            emptyMessage="Get started by creating your first project."
            emptyStateActionText="Create New Project"
            onEmptyStateAction={handleCreateNew}
        />

        {isModalOpen && (
          <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} title={isEditMode ? `Edit Project: ${currentProject.name}` : "Create New Project"} size="lg"
            footer={
              <HStack spacing="sm" justifyContent="flex-end">
                <Button variant="outline" onClick={() => setIsModalOpen(false)} disabled={isSubmitting}>Cancel</Button>
                {isEditMode ? (
                    <Button onClick={handleUpdateSubmit} isLoading={isSubmitting}>Save Changes</Button>
                ) : wizardStep === 1 ? (
                    <Button onClick={handleWizardNext} disabled={isSubmitting}>Next: Secure Project</Button>
                ) : (
                    <Button onClick={handleWizardSubmit} isLoading={isSubmitting}>Generate Keys & Create</Button>
                )}
              </HStack>
            }
          >
            {formError && <Alert variant="error" onDismiss={() => setFormError(null)}>{formError}</Alert>}
            {!isEditMode && <Box mb="lg"><Stepper steps={wizardSteps} activeStepId={wizardStep} /></Box>}
            {isEditMode ? renderFormContent() : (wizardStep === 1 ? renderFormContent() : renderSecurityStep())}
          </Modal>
        )}
        
        {showDeactivateConfirm && projectToProcess && (
          <Modal
            title={`Confirm Deactivation: ${projectToProcess.name}`}
            isOpen={showDeactivateConfirm}
            onClose={() => setShowDeactivateConfirm(false)}
            size="md"
            footer={
              <HStack spacing="md" justifyContent="flex-end">
                <Button variant="outline" onClick={() => setShowDeactivateConfirm(false)} disabled={isSubmitting}>Cancel</Button>
                <Button 
                  variant="destructive" 
                  onClick={() => handleDeactivateReactivate(projectToProcess, false)} 
                  isLoading={isSubmitting}
                  disabled={isSubmitting || confirmationInput !== projectToProcess.id}
                >
                  Deactivate Project
                </Button>
              </HStack>
            }
          >
            <Stack spacing="md">
              <Typography>Are you sure you want to deactivate project "<strong>{projectToProcess.name}</strong>"?</Typography>
              <Alert variant="warning" title="This action is irreversible through the UI.">The project will be marked inactive and hidden from most user views.</Alert>
              <Input
                label={<span>Please type <strong>{projectToProcess.id}</strong> to confirm.</span>}
                id="deactivate-confirm-input"
                value={confirmationInput}
                onChange={(e) => setConfirmationInput(e.target.value)}
                autoFocus
                disabled={isSubmitting}
              />
            </Stack>
          </Modal>
        )}
      </Stack>
    </Card>
  );
}
export default ProjectManagementPage;